// THE SOCIAL LAYER — pure maths. No DOM, no React, and NO ENGINE.
//
// ── WHY THIS FILE IMPORTS NOTHING ────────────────────────────────────────
// Everything here simulates other players: the table's running total, the
// players-online counter, the roster of fake names, the payouts they appear to
// win. None of it may ever touch real money, and the cheapest way to guarantee
// that is for the whole layer to have no way of reaching it. This file imports
// nothing at all — in particular not ./engine, not ./lattice, and not the
// wallet. scripts/assets/roulette_fake_isolation_test.mjs asserts that.
//
// See docs/ROULETTE_UI_V2_SPEC.md §12: simulated players presented as real
// activity is a consumer-protection consideration in some jurisdictions, which
// is why the entire layer is built to be removable in one commit.

/* ─── the table's running total ───────────────────────────────────────────
 * The reference gives two hard points inside one 20s window:
 *     t = 16s (u = 0.20)   17,734
 *     t =  0s (u = 1.00)  161,411
 * A single power law fits both exactly:
 *     17734 / 161411 = 0.10987
 *     p = ln(0.10987) / ln(0.20) = 1.372
 * so V(u) = TARGET * u^1.372, which is why the total visibly ACCELERATES —
 * a linear ticker reads as a progress bar, not as a filling table.
 */
export const TABLE_BET_EXP = 1.372;

/** Per-round target. The reference itself varies — 161,411 / 189,739 / 146,521
 *  — so a fixed ceiling would be the tell that gives the whole thing away. */
export function pickTableTarget(rnd: () => number): number {
  return Math.round(150_000 + rnd() * 50_000);
}

/** The table total at fraction `u` of the betting window (0 = open, 1 = close). */
export function tableBetAt(u: number, target: number): number {
  const k = u <= 0 ? 0 : u >= 1 ? 1 : u;
  return target * Math.pow(k, TABLE_BET_EXP);
}

/**
 * A jittered, MONOTONIC reading of the total.
 *
 * The jitter stops it looking computed; the clamp against the previous value is
 * what actually matters. A table total that ticks DOWN is an instant tell —
 * money does not leave a table mid-round — and mean-reverting noise will
 * produce a downward step eventually unless it is forbidden.
 */
export function nextTableBet(prev: number, u: number, target: number, rnd: () => number): number {
  const base = tableBetAt(u, target);
  const jittered = base * (1 + (rnd() - 0.5) * 0.008);
  return Math.max(prev, jittered);
}

/* ─── players online ──────────────────────────────────────────────────────
 * Observed deltas across the reference shots: +245, -190, +41, -57. Those
 * REVERT — they are not a uniform random walk and not a monotonic climb. A
 * bounded mean-reverting walk reproduces that shape.
 */
export const ONLINE_BASE = 4300;
/* RELATIVE, not absolute. These were BAND=260 / SIGMA=90 -- tuned when every
   table claimed ~4,300 players, where they read as a gentle +/-6% breath. The
   moment the count became the ROOM'S real seat figure (2026-08-31, audit C8),
   the same constants turned into a +/-40% lurch on a 650-seat room: the tile
   could wander 150 below the number the lobby card was showing for the same
   table at the same moment. Scaled to the base they are byte-identical at
   4,300 and proportionate everywhere else. */
export const ONLINE_BAND_FRAC = 260 / 4300;   // ~6% either side
export const ONLINE_PULL = 0.18;
export const ONLINE_SIGMA_FRAC = 90 / 4300;   // ~2% a tick

export function nextOnline(prev: number, base: number, rnd: () => number): number {
  const band = Math.max(10, base * ONLINE_BAND_FRAC);
  const sigma = Math.max(3, base * ONLINE_SIGMA_FRAC);
  const pull = (base - prev) * ONLINE_PULL;
  const noise = (rnd() + rnd() + rnd() - 1.5) * sigma;   // roughly normal
  let next = Math.round(prev + pull + noise);
  const lo = base - band, hi = base + band;
  if (next < lo) next = lo + Math.round(rnd() * 20);
  if (next > hi) next = hi - Math.round(rnd() * 20);
  // NEVER EQUAL. A counter that repeats a value for two ticks reads as a static
  // image, which is worse than not having it at all.
  if (next === prev) next = prev + (rnd() < 0.5 ? -1 : 1);
  return next;
}

/* ─── the roster ──────────────────────────────────────────────────────────
 * THE LEADERBOARD, not a cast list.
 *
 * This used to be three interchangeable seats wearing a "Winner" or "Lucky"
 * sticker that got reshuffled every round. Two things were wrong with it. The
 * stickers meant nothing — they were assigned at random, so a badge carried no
 * information and after two rounds the player stopped reading them. And the
 * faces changed identity every round, so there was nobody to recognise.
 *
 * What replaces it is a STANDING. Three named players with a running total,
 * ranked 1-2-3, and a fourth tile for everybody else. Each round some of them
 * win and the order re-sorts, visibly, in front of the player. That is the
 * whole psychological point: a leaderboard that moves is watched, a static one
 * is wallpaper — and a player who has watched Kajal climb from third to first
 * has been at this table long enough to feel like a regular.
 *
 * Names rotate slowly (see rotateRoster) so the table does not become a fixed
 * cast either.
 */
export const FAKE_NAMES = [
  'Arushi', 'Amrutha', 'Kajal', 'Keerthiraj', 'Divya', 'Rohit',
  'Sneha', 'Vikram', 'Priya', 'Anjali', 'Manish', 'Farhan',
] as const;

/** `face` is the INDEX INTO FAKE_NAMES, never a random number. A given name
 *  must always wear the same face: an avatar that changes hair between rounds
 *  is the single most obvious tell the whole layer has. */
export type Seat = { name: string; face: number; win: number; photo?: string };

/* REAL bot portraits, used RARELY. The platform's bot pool carries ~40 real
 * profile photos (public /uploads/bots/*.webp); a seat occasionally wearing one
 * among the drawn faces reads as "some players upload a picture, most don't" —
 * which is exactly how a real leaderboard looks. Kept rare on the owner's own
 * instruction: a photo every seat, every round, would let a player start
 * recognising the bots across games. */
const BOT_PHOTOS = [
  '/uploads/bots/40_9040c53c-c7cd-40ff-b506-6b64d965d2c6.webp',
  '/uploads/bots/18_2373220a-aafb-497b-8c9e-2e2b9ffc39bf.webp',
  '/uploads/bots/43_6b276ab6-a37c-4e7b-96f1-1497a54337f3.webp',
  '/uploads/bots/15_855ca37a-451a-460d-992a-773829f581ef.webp',
  '/uploads/bots/11_d1b39229-fa11-4db7-b4a9-71c6c05d129f.webp',
  '/uploads/bots/23_c85f1ce7-fb20-431c-99b7-28dddd03144f.webp',
  '/uploads/bots/35_a270aea2-b6f6-4c82-928b-b31f56dcce02.webp',
  '/uploads/bots/39_32f9d9bb-c25f-43f5-b1ed-ed16ccaeda9f.webp',
];
/** ~1 seat in 8, decided per arrival — most rounds show none at all. */
const maybePhoto = (rnd: () => number): string | undefined =>
  rnd() < 0.125 ? BOT_PHOTOS[Math.floor(rnd() * BOT_PHOTOS.length)] : undefined;

/**
 * A seat's opening standing.
 *
 * THE BAND MATTERS MORE THAN THE SHAPE. The first version of this was heavily
 * tailed — 4k / 20k / 90k magnitudes with a 0.5-3.5x spread — on the reasoning
 * that three standings within 10% of each other look generated. True, but it
 * put first place two orders of magnitude above third, and a round's win tops
 * out around 20k. The order could therefore never change, and a leaderboard that
 * never changes is the wallpaper this was built to stop being.
 * roulette_fake_isolation_test.mjs asserts the reshuffle for exactly that reason,
 * and it caught this.
 *
 * So: still spread, but inside one order of magnitude — roughly 10k to 80k,
 * against round wins of 50 to 22,500. Gaps of that size close in a handful of
 * rounds and a big hit jumps a place outright, which is what the player is meant
 * to sit and watch.
 */
function seedWin(rnd: () => number): number {
  const d = rnd();
  const mag = d < 0.55 ? 14_000 : d < 0.9 ? 30_000 : 55_000;
  return Math.round((mag * (0.7 + rnd() * 0.8)) / 100) * 100;
}

/** Three distinct players, already in rank order. */
export function rollRoster(rnd: () => number): Seat[] {
  const idx = [...FAKE_NAMES.keys()];
  const picked: number[] = [];
  for (let i = 0; i < 3; i++) picked.push(...idx.splice(Math.floor(rnd() * idx.length), 1));
  return picked
    .map((i) => ({ name: FAKE_NAMES[i], face: i, win: seedWin(rnd), photo: maybePhoto(rnd) }))
    .sort((a, b) => b.win - a.win);
}

/**
 * One round's result, applied to the standing.
 *
 * Returns the new order AND what each named player just won, so the tiles can
 * float a `+₹x` on the seat it actually belongs to. Keyed by NAME rather than
 * by position, because the whole point is that positions are about to change.
 *
 * Not everyone wins. `0` means that player got nothing this round, which is
 * what stops the board from looking like a payout script — and it is also what
 * makes the re-sort meaningful, since a rank only moves when someone above is
 * standing still.
 */
export function applyRoundWins(
  seats: Seat[], rnd: () => number,
): { seats: Seat[]; gains: Record<string, number> } {
  const gains: Record<string, number> = {};
  for (const s of seats) {
    if (rnd() < 0.34) { gains[s.name] = 0; continue; }
    const decade = rnd();
    const mag = decade < 0.45 ? 100 : decade < 0.85 ? 1_000 : 5_000;
    gains[s.name] = Math.round((mag * (0.5 + rnd() * 4)) / 10) * 10;
  }
  const next = seats
    .map((s) => ({ ...s, win: s.win + gains[s.name] }))
    .sort((a, b) => b.win - a.win);
  return { seats: next, gains };
}

/**
 * TURNOVER — roughly one seat in eight rounds, and NOT always the last one.
 *
 * The first version only ever replaced the bottom seat, which is the obvious
 * move and it is wrong. Standings here are a running sum: everyone gains at
 * about the same average rate, so the gaps between them grow with the square
 * root of the round count and after twenty rounds the leader is unreachable.
 * Only ever swapping the seat that is already last cannot fix that — the top of
 * the board freezes and the player is watching a photograph. The isolation test
 * asserts the reshuffle and it caught this too.
 *
 * What actually happens at a table is that big winners LEAVE. So the seat that
 * goes is weighted towards the bottom but the leader can cash out as well, and
 * when they do, the two below them move up — a visible reshuffle that costs
 * nothing and is the most believable thing on the board.
 *
 * The replacement still arrives near the BOTTOM of the current band. Someone
 * parachuting straight into first place is the tell; someone sitting down and
 * having to climb is a table.
 */
export function rotateRoster(seats: Seat[], rnd: () => number): Seat[] {
  /* 0.4, up from 0.125 (owner, 2026-09-01): the cast should visibly refresh
     every few results — a table whose top three never change is its own tell. */
  if (rnd() > 0.4) return seats;
  const here = new Set(seats.map((s) => s.name));
  const free = [...FAKE_NAMES.keys()].filter((i) => !here.has(FAKE_NAMES[i]));
  if (!free.length) return seats;

  const r = rnd();
  const leaves = r < 0.55 ? seats.length - 1 : r < 0.85 ? 1 : 0;
  const i = free[Math.floor(rnd() * free.length)];
  const floor = seats[seats.length - 1].win;
  const next = seats.filter((_, k) => k !== leaves);
  next.push({ name: FAKE_NAMES[i], face: i, win: Math.round(floor * (0.55 + rnd() * 0.4)), photo: maybePhoto(rnd) });
  return next.sort((a, b) => b.win - a.win);
}

/* ─── where the chips come from ───────────────────────────────────────────
 * The crowd tile is FOUR THOUSAND people and the three named seats are three
 * people, so almost every chip has to come off the crowd tile or the arithmetic
 * on screen contradicts the animation. The owner put it exactly right: most of
 * the coins come from the total, and only a few from the top three.
 *
 * 78/22 rather than 99/1 because the named seats have to keep moving — a tile
 * that never emits stops reading as a player and starts reading as a photo.
 */
export const CROWD_SHARE = 0.78;

/** 0, 1, 2 = the ranked seats · 3 = the crowd tile. */
export function pickLauncher(rnd: () => number): 0 | 1 | 2 | 3 {
  if (rnd() < CROWD_SHARE) return 3;
  return Math.floor(rnd() * 3) as 0 | 1 | 2;
}

/** `1,234.00`, the reference's format. */
export function money2(v: number): string {
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * `₹1.61L` — the crowd tile's headline.
 *
 * The tile is one gutter wide and the table total runs to six figures, so the
 * exact `161,411.00` cannot be set at a size anyone would call a big number.
 * Indian short scale keeps it BIG, which is the job: the number is there to be
 * felt, not audited, and the player's own stake — the one that has to be exact
 * — is on the shelf in full.
 */
export function moneyShort(v: number): string {
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}k`;
  return `₹${Math.round(v)}`;
}
